import { Injectable } from '@angular/core';
import { TrainingCourseState } from 'app/_models/training/training-course-state';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingType } from 'app/_models/training/training-type';

interface SiteTrainingSummary {
  inductionCourse: any;
  siteOwner: any;
  siteNames: Set<string>;
}

export interface TrainingRowAction {
  label: 'start' | 'continue' | 'take again';
  style: 'primary' | 'secondary';
  kind: 'navigate' | 'create';
  userCourseId?: number;
  courseId?: number;
  workerUserId?: number;
}

export interface WorkerTrainingRowVm {
  trainingId: number;
  trainingName: string;
  trainingType: TrainingType;
  siteOwnerName: string;
  primaryCourse: TrainingCourseForUser;
  statusLabel: string;
  statusBadgeClass: string;
  statusIcon: 'success' | 'danger';
  expiryPrefix: string | null;
  expiryDate: Date | null;
  requiredSiteCount: number;
  requiredSites: string[];
  action: TrainingRowAction | null;
  deletableCourse: TrainingCourseForUser | null;
}

@Injectable()
export class WorkerTrainingListViewModelService {
  public buildTrainingRows(
    courses: TrainingCourseForUser[],
    workerSites: any[],
    currentWorkerUserId: number,
  ): WorkerTrainingRowVm[] {
    const preparedCourses = this.prepareCourses(courses, workerSites, currentWorkerUserId);
    const groupedCourses = this.groupCoursesByTrainingId(preparedCourses);
    return groupedCourses.map(group => this.mapTrainingRow(group, currentWorkerUserId));
  }

  private prepareCourses(
    courses: TrainingCourseForUser[],
    workerSites: any[],
    currentWorkerUserId: number,
  ): TrainingCourseForUser[] {
    // Collect a per-training-course view of the worker's sites and required induction metadata
    const siteSummaries = this.buildSiteTrainingSummary(workerSites);

    // Inject any additional worker-specific information from the site summaries into any existing course attempts
    const enrichedCourses = courses.map(course => this.applySiteSummary(course, siteSummaries));

    // Create synthetic "Not started" entries for inductions that are required but have no attempt
    const placeholders = this.buildInductionPlaceholders(enrichedCourses, siteSummaries, currentWorkerUserId);

    return [...placeholders, ...enrichedCourses];
  }

  // flips the worker's sites list into a map of training ids to sites that require those training courses
  private buildSiteTrainingSummary(workerSites: any[]): Map<number, SiteTrainingSummary> {
    const summary = new Map<number, SiteTrainingSummary>(); // trainingId => summary of training and sites

    workerSites.forEach(siteData => {
      const site = siteData && siteData.site;

      // can ignore sites with no induction
      const inductionCourse = site && site.siteInductionCourse;
      if (!inductionCourse) {
        return;
      }

      const trainingId = inductionCourse.id;
      if (!summary.has(trainingId)) {
        summary.set(trainingId, {
          inductionCourse,
          siteOwner: site.siteOwner,
          siteNames: new Set<string>(),
        });
      }

      // add this site to the collection
      const info = summary.get(trainingId);
      info.siteNames.add(site.name);
    });

    return summary;
  }

  private applySiteSummary(
    course: TrainingCourseForUser,
    summary: Map<number, SiteTrainingSummary>,
  ): TrainingCourseForUser {
    if (!course || !course.course || course.course.type !== TrainingType.Induction) {
      return course;
    }

    const info = summary.get(course.course.trainingId);

    // user isn't on any sites associated with this training course (may have been removed from site)
    if (!info) {
      course.course.sites = [];
      return course;
    }

    // add list of sites the course is an induction for which the user is also on
    course.course.sites = Array.from(info.siteNames).sort((a, b) => a.localeCompare(b));

    return course;
  }

  private buildInductionPlaceholders(
    existingCourses: TrainingCourseForUser[],
    summary: Map<number, SiteTrainingSummary>,
    currentWorkerUserId: number,
  ): TrainingCourseForUser[] {
    const presentIds = new Set<number>(
      existingCourses
        .filter(course => course && course.course)
        .map(course => course.course.trainingId),
    );

    const placeholders: TrainingCourseForUser[] = [];
    summary.forEach((info, trainingId) => {
      if (presentIds.has(trainingId)) {
        return;
      }

      placeholders.push(
        TrainingCourseForUser.createInductionPlaceholder({
          workerUserId: currentWorkerUserId,
          inductionCourse: info.inductionCourse,
          siteOwner: info.siteOwner,
          siteNames: Array.from(info.siteNames).sort((a, b) => a.localeCompare(b)),
        }),
      );
    });

    return placeholders;
  }

  private groupCoursesByTrainingId(courses: TrainingCourseForUser[]): TrainingCourseForUser[][] {
    const grouped = new Map<number, TrainingCourseForUser[]>();

    courses.forEach(course => {
      if (!course || !course.course || !course.course.trainingId) {
        return;
      }
      const trainingId = course.course.trainingId;
      if (!grouped.has(trainingId)) {
        grouped.set(trainingId, []);
      }
      grouped.get(trainingId).push(course);
    });

    const groupedArrays = Array.from(grouped.values()).map(group => this.normalizeCourseGroup(group));

    groupedArrays.sort((a, b) => {
      const nameA = a[0].course.trainingName;
      const nameB = b[0].course.trainingName;
      return nameA.localeCompare(nameB);
    });

    return groupedArrays;
  }

  private normalizeCourseGroup(courses: TrainingCourseForUser[]): TrainingCourseForUser[] {
    this.sortCourses(courses);
    return this.pruneCourseAttempts(courses);
  }

  private sortCourses(courses: TrainingCourseForUser[]): void {
    const progressOrder = {
      [TrainingCourseState.NotStarted]: 0,
      [TrainingCourseState.InProgress]: 1,
      [TrainingCourseState.Completed]: 2,
      [TrainingCourseState.Expired]: 3,
    };

    courses.sort((a, b) => {
      const progressDiff = progressOrder[a.courseProgress] - progressOrder[b.courseProgress];
      if (progressDiff !== 0) {
        return progressDiff;
      }

      const lastModifiedA = a.lastModifiedDate ? a.lastModifiedDate.getTime() : 0;
      const lastModifiedB = b.lastModifiedDate ? b.lastModifiedDate.getTime() : 0;
      if (lastModifiedA !== lastModifiedB) {
        return lastModifiedB - lastModifiedA;
      }

      if (a.expiryDate && b.expiryDate) {
        const expiryDiff = b.expiryDate.getTime() - a.expiryDate.getTime();
        if (expiryDiff !== 0) {
          return expiryDiff;
        }
      }

      return (b.workerUserCourseId || 0) - (a.workerUserCourseId || 0);
    });
  }

  private pruneCourseAttempts(courses: TrainingCourseForUser[]): TrainingCourseForUser[] {
    let hasNotStarted = false;
    let hasInProgress = false;
    let hasCompleted = false;
    let hasExpired = false;

    const filtered: TrainingCourseForUser[] = [];

    courses.forEach(course => {
      switch (course.courseProgress) {
        case TrainingCourseState.NotStarted:
          if (!hasNotStarted) {
            filtered.push(course);
            hasNotStarted = true;
          }
          break;
        case TrainingCourseState.InProgress:
          if (!hasInProgress) {
            filtered.push(course);
            hasInProgress = true;
          }
          break;
        case TrainingCourseState.Completed:
          if (!hasCompleted) {
            filtered.push(course);
            hasCompleted = true;
          }
          break;
        case TrainingCourseState.Expired:
          if (!hasCompleted && !hasExpired) {
            filtered.push(course);
            hasExpired = true;
          }
          break;
        default:
          filtered.push(course);
      }
    });

    const hasActive = hasNotStarted || hasInProgress;
    filtered.forEach(filteredCourse => {
      filteredCourse.hasOneInProgress = hasActive;
    });

    return filtered;
  }

  private getStatusBadgeClass(course: TrainingCourseForUser): string {
    switch (course.courseProgress) {
      case TrainingCourseState.Completed:
        return 'badge-success';
      case TrainingCourseState.InProgress:
        return 'badge-info';
      case TrainingCourseState.NotStarted:
        return 'badge-secondary';
      case TrainingCourseState.Expired:
        return 'badge-danger';
      default:
        return 'badge-light';
    }
  }

  private getStatusLabel(course: TrainingCourseForUser): string {
    switch (course.courseProgress) {
      case TrainingCourseState.NotStarted:
        return 'Not started';
      case TrainingCourseState.InProgress:
        return 'In progress';
      case TrainingCourseState.Completed:
        return 'Completed';
      case TrainingCourseState.Expired:
        return 'Expired';
      default:
        return 'Unknown';
    }
  }

  private mapTrainingRow(
    trainingCourses: TrainingCourseForUser[],
    currentWorkerUserId: number,
  ): WorkerTrainingRowVm {
    const courseRoles = this.getCourseRoles(trainingCourses);
    const requiredSites = courseRoles.primary.course.sites || [];
    const primaryCourse = courseRoles.primary;
    const actionCourse = courseRoles.action;

    return {
      trainingId: primaryCourse.course.trainingId,
      trainingName: primaryCourse.course.trainingName,
      trainingType: primaryCourse.course.type,
      siteOwnerName: primaryCourse.course.siteOwner.fullName,
      primaryCourse,
      statusLabel: this.getStatusLabel(primaryCourse),
      statusBadgeClass: this.getStatusBadgeClass(primaryCourse),
      statusIcon: primaryCourse.courseProgress === TrainingCourseState.Completed ? 'success' : 'danger',
      ...this.getExpiryDisplay(trainingCourses, primaryCourse),
      requiredSiteCount: requiredSites.length,
      requiredSites,
      action: this.buildRowAction(primaryCourse, actionCourse, currentWorkerUserId),
      deletableCourse: primaryCourse.courseProgress === TrainingCourseState.InProgress ? primaryCourse : null,
    };
  }

  private getCourseRoles(trainingCourses: TrainingCourseForUser[]): {
    primary: TrainingCourseForUser;
    action: TrainingCourseForUser;
  } {
    const primaryCourse = this.findPrimaryCourse(trainingCourses);
    const actionCourse = this.findActionCourse(trainingCourses, primaryCourse);
    return {
      primary: primaryCourse,
      action: actionCourse,
    };
  }

  private getExpiryDisplay(
    trainingCourses: TrainingCourseForUser[],
    primaryCourse: TrainingCourseForUser,
  ): { expiryPrefix: string | null; expiryDate: Date | null } {
    if (primaryCourse.courseProgress === TrainingCourseState.InProgress) {
      const currentPassCourse = this.findCourseByProgress(trainingCourses, TrainingCourseState.Completed);
      if (currentPassCourse && currentPassCourse.expiryDate) {
        return {
          expiryPrefix: 'Current pass valid until',
          expiryDate: currentPassCourse.expiryDate,
        };
      }

      const previousPassCourse = this.findCourseByProgress(trainingCourses, TrainingCourseState.Expired);
      if (previousPassCourse && previousPassCourse.expiryDate) {
        return {
          expiryPrefix: 'Previous pass expired on',
          expiryDate: previousPassCourse.expiryDate,
        };
      }

      return { expiryPrefix: null, expiryDate: null };
    }

    if (!primaryCourse.expiryDate) {
      return { expiryPrefix: null, expiryDate: null };
    }

    return primaryCourse.courseProgress === TrainingCourseState.Expired
      ? { expiryPrefix: 'Expired on', expiryDate: primaryCourse.expiryDate }
      : { expiryPrefix: 'Valid until', expiryDate: primaryCourse.expiryDate };
  }

  private findPrimaryCourse(trainingCourses: TrainingCourseForUser[]): TrainingCourseForUser {
    return this.findCourseByProgress(trainingCourses, TrainingCourseState.InProgress)
      || this.findCourseByProgress(trainingCourses, TrainingCourseState.Completed)
      || this.findCourseByProgress(trainingCourses, TrainingCourseState.Expired)
      || this.findCourseByProgress(trainingCourses, TrainingCourseState.NotStarted)
      || trainingCourses[0];
  }

  private findActionCourse(
    trainingCourses: TrainingCourseForUser[],
    primaryCourse: TrainingCourseForUser,
  ): TrainingCourseForUser {
    return this.findCourseByProgress(trainingCourses, TrainingCourseState.InProgress)
      || trainingCourses.find(course => course.courseProgress === TrainingCourseState.NotStarted && !!course.workerUserCourseId)
      || primaryCourse;
  }

  private buildRowAction(
    primaryCourse: TrainingCourseForUser,
    actionCourse: TrainingCourseForUser,
    currentWorkerUserId: number,
  ): TrainingRowAction | null {
    switch (actionCourse.courseProgress) {
      case TrainingCourseState.InProgress:
        return {
          label: 'continue',
          style: 'primary',
          kind: 'navigate',
          userCourseId: actionCourse.workerUserCourseId,
        };
      case TrainingCourseState.NotStarted:
        return {
          label: primaryCourse.courseProgress === TrainingCourseState.Completed ? 'take again' : 'start',
          style: primaryCourse.courseProgress === TrainingCourseState.Completed ? 'secondary' : 'primary',
          kind: actionCourse.workerUserCourseId ? 'navigate' : 'create',
          userCourseId: actionCourse.workerUserCourseId,
          courseId: actionCourse.course.trainingId,
          workerUserId: actionCourse.workerId || currentWorkerUserId,
        };
      case TrainingCourseState.Expired:
        if (actionCourse.hasOneInProgress) {
          return null;
        }
        return {
          label: 'start',
          style: 'primary',
          kind: 'create',
          courseId: actionCourse.course.trainingId,
          workerUserId: actionCourse.workerId || currentWorkerUserId,
        };
      case TrainingCourseState.Completed:
        if (actionCourse.hasOneInProgress) {
          return null;
        }
        return {
          label: 'take again',
          style: 'secondary',
          kind: 'create',
          courseId: actionCourse.course.trainingId,
          workerUserId: actionCourse.workerId || currentWorkerUserId,
        };
      default:
        return null;
    }
  }

  private findCourseByProgress(
    trainingCourses: TrainingCourseForUser[],
    progress: TrainingCourseState,
  ): TrainingCourseForUser | undefined {
    return trainingCourses.find(course => course.courseProgress === progress);
  }
}
